import { useState } from 'react'
import { motion } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import ProfileDropdown from '../components/ProfileDropdown'
import { 
  CameraIcon,
  Bars3Icon,
  XMarkIcon,
  SparklesIcon,
  BoltIcon,
  WrenchScrewdriverIcon,
  ArrowLeftIcon
} from '@heroicons/react/24/outline'

const UpdatesPage = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)

  // Check if user is logged in
  const isLoggedIn = !!user

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Floating orbs for glassmorphism background effect */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="floating-orb floating-orb-1"></div>
        <div className="floating-orb floating-orb-2"></div>
        <div className="floating-orb floating-orb-3"></div>
        <div className="floating-orb floating-orb-4"></div>
        <div className="floating-orb floating-orb-5"></div>
      </div>

      {/* Header */}
      <header className="relative z-20 glass-header">
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4 lg:py-6">
            <div className="flex items-center space-x-3">
              <motion.button 
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 lg:space-x-3 hover:opacity-80 transition-opacity"
              >
                <div className="w-10 h-10 lg:w-12 lg:h-12 glass-badge rounded-2xl flex items-center justify-center">
                  <CameraIcon className="w-5 h-5 lg:w-6 lg:h-6 text-white" />
                </div>
                <span className="text-xl lg:text-2xl font-bold text-white">
                  Shoot<span className="text-gray-400">Suite</span>
                </span>
              </motion.button>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center space-x-8">
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                onClick={() => navigate('/')}
                className="text-white/80 hover:text-white transition-colors font-medium"
              >
                Home
              </motion.button>
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                onClick={() => navigate('/roadmap')}
                className="text-white/80 hover:text-white transition-colors font-medium"
              >
                Roadmap
              </motion.button>
              <motion.button
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="text-white font-medium"
              >
                Updates
              </motion.button>
              
              {isLoggedIn ? (
                <ProfileDropdown />
              ) : (
                <div className="flex items-center space-x-4">
                  <motion.button
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    onClick={() => navigate('/auth')}
                    className="text-white/80 hover:text-white transition-colors font-medium"
                  >
                    Sign In
                  </motion.button>
                  <motion.button
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    onClick={() => navigate('/auth')}
                    className="gradient-button"
                  >
                    Get Started
                  </motion.button>
                </div>
              )}
            </div>

            {/* Mobile menu button */}
            <div className="lg:hidden">
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-white p-2"
              >
                {isMobileMenuOpen ? (
                  <XMarkIcon className="w-6 h-6" />
                ) : (
                  <Bars3Icon className="w-6 h-6" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="lg:hidden border-t border-white/10 pt-4 pb-6"
            >
              <div className="flex flex-col space-y-4">
                <button
                  onClick={() => {
                    navigate('/')
                    setIsMobileMenuOpen(false)
                  }}
                  className="text-white/80 hover:text-white transition-colors font-medium text-left"
                >
                  Home
                </button>
                <button
                  onClick={() => {
                    navigate('/roadmap')
                    setIsMobileMenuOpen(false)
                  }}
                  className="text-white/80 hover:text-white transition-colors font-medium text-left"
                >
                  Roadmap
                </button>
                <span className="text-white font-medium">Updates</span>
                
                {!isLoggedIn && (
                  <>
                    <button
                      onClick={() => {
                        navigate('/auth')
                        setIsMobileMenuOpen(false)
                      }}
                      className="text-white/80 hover:text-white transition-colors font-medium text-left"
                    >
                      Sign In
                    </button>
                    <button
                      onClick={() => {
                        navigate('/auth')
                        setIsMobileMenuOpen(false)
                      }}
                      className="gradient-button text-center"
                    >
                      Get Started
                    </button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </nav>
      </header>

      {/* Main Content */}
      <main className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-16">
        {/* Back Button */}
        <motion.button
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          onClick={() => navigate(-1)}
          className="flex items-center space-x-2 text-white/60 hover:text-white transition-colors mb-8 group"
        >
          <ArrowLeftIcon className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
          <span>Back</span>
        </motion.button>

        {/* In Progress Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mb-8"
        >
          <div className="flex items-center space-x-3 mb-6">
            <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg flex items-center justify-center">
              <SparklesIcon className="w-4 h-4 text-white" />
            </div>
            <h2 className="text-2xl font-bold text-white">In Progress</h2>
          </div>
          <div className="space-y-3">
            {[
              {
                title: "Advanced Calendar Integration",
                description: "Seamless integration with Google/Outlook, conflict detection, and weather forecasts.",
                tag: "Calendar"
              },
              {
                title: "Automated Email Sequences",
                description: "Smart email automation for onboarding, updates, and follow-ups with A/B testing.",
                tag: "Email"
              },
              {
                title: "Advanced Analytics Dashboard",
                description: "Business intelligence with revenue forecasting and profitability tracking.",
                tag: "Analytics"
              },
              {
                title: "Team Collaboration Tools",
                description: "Multi-user workspace with role permissions, chat, and collaborative planning.",
                tag: "Collaboration"
              }
            ].map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass-badge rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group hover:translate-x-2 border-l-4 border-gradient-to-b from-purple-500 to-blue-500"
              >
                <div className="text-white/90 text-base leading-relaxed">
                  <strong>{feature.title}</strong> - {feature.description}
                  <span className="inline-block bg-purple-500/20 text-purple-400 px-3 py-1 rounded-full text-xs font-medium mt-2">
                    {feature.tag}
                  </span>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Patch Notes Container */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="glass-mockup rounded-3xl p-6 lg:p-10"
        >
          {/* Header */}
          <div className="text-center mb-10">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3 }}
              className="inline-block bg-gradient-to-r from-blue-500 to-purple-600 text-white px-6 py-2 rounded-full text-sm font-semibold mb-4 shadow-lg"
            >
              Version 2.0.0
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent mb-2"
            >
              ShootSuite
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-lg text-white/70 mb-2"
            >
              Major Update: Invoice System Overhaul & Mobile Optimization
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="text-sm text-white/50"
            >
              Released May 27, 2025
            </motion.p>
          </div>

          {/* New Features Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="mb-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-emerald-500 to-cyan-500 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">New Features</h2>
            </div>
            <div className="space-y-3">
              {[
                {
                  title: "Professional PDF Invoice Generation",
                  description: "Generate clean, professional PDF invoices with company branding, itemized tables, and financial summaries using jsPDF library",
                  tag: "Invoice System"
                },
                {
                  title: "Real-time Dashboard Income Updates",
                  description: "Monthly income automatically updates on dashboard when invoices are marked as paid, with custom event system",
                  tag: "Dashboard"
                },
                {
                  title: "Built-in Notification System",
                  description: "Replaced browser alerts with elegant in-app notifications for better user experience across all invoice operations",
                  tag: "UX/UI"
                },
                {
                  title: "Revenue Analytics Auto-refresh",
                  description: "Revenue analytics modal now automatically refreshes when income data changes, ensuring accurate real-time reporting",
                  tag: "Analytics"
                },
                {
                  title: "Comprehensive Mobile Optimization",
                  description: "Complete mobile optimization for all pages with responsive layouts, touch-friendly controls, and iOS Safari compatibility",
                  tag: "Mobile"
                },
                {
                  title: "User Profile Persistence System",
                  description: "Advanced caching system prevents profile data loss with automatic retry mechanisms and background refresh",
                  tag: "Authentication"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.8 + index * 0.1 }}
                  className="glass-badge rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group hover:translate-x-2 border-l-4 border-gradient-to-b from-blue-500 to-purple-600"
                >
                  <div className="text-white/90 text-base leading-relaxed">
                    <strong>{feature.title}</strong> - {feature.description}
                    <span className="inline-block bg-blue-500/20 text-blue-400 px-3 py-1 rounded-full text-xs font-medium mt-2">
                      {feature.tag}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Improvements Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.2 }}
            className="mb-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <BoltIcon className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Improvements</h2>
            </div>
            <div className="space-y-3">
              {[
                "PDF Generation - Professional invoice layout with company header, billing info, itemized tables, and terms & conditions",
                "Invoice Workflow - Enhanced status management with real-time updates and improved error handling",
                "Mobile Experience - Touch-optimized controls, responsive tables, and iOS Safari zoom prevention",
                "Dashboard Analytics - Improved monthly income calculations with fallback date handling",
                "User Authentication - Robust profile data caching with 5-minute validity and automatic cleanup",
                "Notification System - Consistent user feedback across all operations with success/error states",
                "Revenue Analytics - Fixed column name mappings and date calculations for accurate reporting",
                "Background Refresh - Periodic user data refresh every 2 minutes to maintain data consistency"
              ].map((improvement, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.3 + index * 0.1 }}
                  className="glass-badge rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group hover:translate-x-2 border-l-4 border-gradient-to-b from-blue-500 to-purple-600"
                >
                  <div className="text-white/90 text-base leading-relaxed">
                    {improvement}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Bug Fixes Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.7 }}
            className="mb-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                <WrenchScrewdriverIcon className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Bug Fixes</h2>
            </div>
            <div className="space-y-3">
              {[
                "Fixed user profile reverting to freemium status due to temporary network errors",
                "Resolved revenue analytics not refreshing when dashboard income updates",
                "Fixed PDF generation fallback when autoTable plugin fails to load",
                "Corrected analytics service column name mappings (total_amount vs total)",
                "Fixed mobile table layouts and touch target sizing issues",
                "Resolved notification system replacing all browser alert popups",
                "Fixed invoice status change events not propagating to dashboard",
                "Corrected date handling in monthly income calculations with null payment dates"
              ].map((fix, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 1.8 + index * 0.1 }}
                  className="glass-badge rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group hover:translate-x-2 border-l-4 border-gradient-to-b from-orange-500 to-red-500"
                >
                  <div className="text-white/90 text-base leading-relaxed">
                    {fix}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Technical Improvements Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.2 }}
            className="mb-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                <WrenchScrewdriverIcon className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Technical Improvements</h2>
            </div>
            <div className="space-y-3">
              {[
                "Added jsPDF and jsPDF-autotable libraries for professional PDF generation",
                "Implemented localStorage caching system with timestamp validation",
                "Enhanced error handling with retry mechanisms and graceful degradation",
                "Added custom event system for real-time data synchronization",
                "Improved mobile CSS with touch manipulation and webkit optimizations",
                "Enhanced authentication context with periodic refresh and cleanup",
                "Added comprehensive debugging and error logging throughout the system"
              ].map((improvement, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 2.3 + index * 0.1 }}
                  className="glass-badge rounded-xl p-4 hover:bg-white/10 transition-all duration-300 group hover:translate-x-2 border-l-4 border-gradient-to-b from-purple-500 to-pink-500"
                >
                  <div className="text-white/90 text-base leading-relaxed">
                    {improvement}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Statistics */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 2.7 }}
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8"
          >
            {[
              { number: "6", label: "Major Features" },
              { number: "8", label: "Improvements" },
              { number: "8", label: "Bug Fixes" },
              { number: "100%", label: "Mobile Optimized" }
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 2.8 + index * 0.1 }}
                className="glass-badge rounded-2xl p-6 text-center"
              >
                <div className="text-3xl font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">
                  {stat.number}
                </div>
                <div className="text-white/70 text-sm mt-1">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Previous Version Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.2 }}
            className="mb-8 border-t border-white/10 pt-8"
          >
            <div className="flex items-center space-x-3 mb-6">
              <div className="w-8 h-8 bg-gradient-to-r from-gray-500 to-gray-600 rounded-lg flex items-center justify-center">
                <SparklesIcon className="w-4 h-4 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-white">Previous Version (1.0.3)</h2>
              <span className="text-sm text-white/50">January 18, 2025</span>
            </div>
            <div className="space-y-3">
              {[
                {
                  title: "Glassmorphism Favicon Design",
                  description: "Updated website favicon with modern glassmorphism styling to match site aesthetic",
                  tag: "UI/UX"
                },
                {
                  title: "Link Embed Branding Update",
                  description: "Updated Open Graph and Twitter meta tags from FreelanceFlow to ShootSuite branding",
                  tag: "Branding"
                },
                {
                  title: "Updates Page Implementation",
                  description: "Added dedicated updates page with patch notes styling and navigation integration",
                  tag: "Feature"
                }
              ].map((feature, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 3.3 + index * 0.1 }}
                  className="glass-badge rounded-xl p-4 hover:bg-white/5 transition-all duration-300 border-l-4 border-gray-500/50"
                >
                  <div className="text-white/70 text-base leading-relaxed">
                    <strong>{feature.title}</strong> - {feature.description}
                    <span className="inline-block bg-gray-500/20 text-gray-400 px-3 py-1 rounded-full text-xs font-medium mt-2">
                      {feature.tag}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Footer */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 3.7 }}
            className="text-center pt-8 border-t border-white/10"
          >
            <p className="text-white/50 text-sm">
              Thank you for using ShootSuite! This major update represents months of development focused on reliability, mobile experience, and professional invoice management.
            </p>
          </motion.div>
        </motion.div>
      </main>
    </div>
  )
}

export default UpdatesPage 